import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../charts/echarts/ColorProvider'
import Tooltip from '../../../charts/echarts/Tooltip'
import PieChart from '../../../charts/echarts/PieChart'
import { useFormattingContext } from '../../../organisms/FormattingProvider/FormattingContext'
import { buildLegendConfig, buildLabelConfig, EMPTY_OBJ } from './chartLayoutUtils'

function PieChartLayout ({ config, data, styleOverrides }) {
  const {
    height = 400,
    colorTheme = 'primary',
    nameAccessor,
    valueAccessor,
    radius = '75%',
    innerRadius = '0%',
    roseType = null,
    tooltip = {},
    display = {},
    overrides = null,
    seriesOverrides = null
  } = config

  const variantProps = styleOverrides?.props ?? {}
  const variantStyles = styleOverrides?.styles ?? {}
  const effectiveInnerRadius = variantProps.innerRadius ?? innerRadius

  const { label: displayLabel = EMPTY_OBJ, legend: displayLegend = EMPTY_OBJ, grid: displayGrid = EMPTY_OBJ } = display

  const legendConfig = buildLegendConfig(displayLegend)

  const { formatter: fmtFn } = useFormattingContext()
  const labelConfig = useMemo(() => {
    if (variantStyles.label?.show === false) return { show: false }
    const base = buildLabelConfig(displayLabel)
    if (displayLabel.format) {
      return { ...base, formatter: (params) => fmtFn(params.value, displayLabel.format) }
    }
    return base
  }, [displayLabel, fmtFn, variantStyles.label])

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || !nameAccessor || !valueAccessor) return []
    return data.map(row => ({
      name: get(row, nameAccessor),
      value: Number(get(row, valueAccessor)) || 0
    }))
  }, [data, nameAccessor, valueAccessor])

  return (
    <ChartColorProvider theme={colorTheme}>
      <ChartProvider height={height} loadingTemplate='pie' {...(legendConfig && { legend: legendConfig })} {...(overrides || {})}>
        <Tooltip
          trigger={tooltip.trigger || 'item'}
          {...(tooltip.valueFormat && { valueFormat: tooltip.valueFormat })}
        />
        <PieChart
          seriesId='pie'
          data={chartData}
          radius={[effectiveInnerRadius, radius]}
          {...(roseType && { roseType })}
          left={displayGrid.left ?? '10%'}
          top={displayGrid.top ?? '10%'}
          right={displayGrid.right ?? '10%'}
          bottom={displayGrid.bottom ?? '10%'}
          label={labelConfig}
          {...(variantStyles.labelLine?.show === false && { labelLine: { show: false } })}
          {...(variantProps.padAngle != null && { padAngle: variantProps.padAngle })}
          {...(seriesOverrides || {})}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

PieChartLayout.propTypes = {
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  styleOverrides: PropTypes.object
}

export default PieChartLayout
